import React, { useState } from 'react';
import { Box, HStack, Image } from '@chakra-ui/react';
import LeafIcon from '../../../../../../assets/leaf-icon.svg';
import Button from 'src/components/Button';

const AwardModalOnSidePanel = (props) => {
  const { handleCreatePoint, onClose, previousPoint, isLoading, title, dailyLimits, alreadyGivePoint } = props;
  const [selectedPoints, setSelectedPoints] = useState(previousPoint || 1);
  const [hasUserSelected, setHasUserSelected] = useState(false);

  const pointOptions = [
    { value: 1, label: '+1', description: 'Good point' },
    { value: 2, label: '+2', description: 'Really helpful' },
    { value: 3, label: '+3', description: 'Nailed it' },
    { value: 4, label: '+4', description: 'This is so good' },
    { value: 5, label: '+5', description: 'Incredible effort' }
  ];

  const getPointLevel = (value) => {
    if (value === 1) return 'Appreciated';
    if (value === 2) return 'Meaningful';
    if (value === 3) return 'Impactful';
    if (value === 4) return 'Inspiring';
    if (value === 5) return 'Exceptional';
    return 'Appreciated';
  };

  const handlePointSelection = (value) => {
    if (value !== selectedPoints) {
      setSelectedPoints(value);
      setHasUserSelected(true);
    }
  };

  const displayPoints = hasUserSelected
    ? selectedPoints
    : previousPoint > 1
      ? previousPoint
      : selectedPoints;

  // Check if user can award based on daily limits
  const canAwardPoints = dailyLimits ? dailyLimits.canAward : true;
  const maxDailyLimit = dailyLimits ? dailyLimits.maxDailyPerRecipient : 5;
  const remainingPoints = dailyLimits ? dailyLimits.remainingForRecipient : maxDailyLimit;

  // Disable point selection buttons that exceed remaining daily limit or when already awarded
  const isPointDisabled = (pointValue) => {
    if (alreadyGivePoint) {
      return pointValue !== previousPoint; // Only allow the previously awarded amount
    }
    if (!dailyLimits) return false;
    return pointValue > dailyLimits.remainingForRecipient;
  };
  return (
    <Box
      backdropFilter={'blur(10px)'}
      background={'rgba(255, 255, 255, 0.719)'}
      position={'absolute'}
      top="0"
      right="0"
      bottom="0"
      zIndex="1000"
      w={'100%'}
      h={'100%'}
      minW={'300px'}
    >
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        display={'flex'}
        flexDir={'column'}
        alignItems={'center'}
        p={4}
        gap={'24px'}
        textAlign={'center'}
        maxW={'408px'}
        borderRadius={'12px'}
        bgColor={'#FFFFFF'}
        border={'1px solid #E5E7EB'}
        boxShadow={'0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)'}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-col items-center gap-1">
            <Image className="w-[40px] h-[40px]" src={LeafIcon} alt="Twigs" />
            <p className="text-[24px] font-[800] text-growth-green-500">Give Twigs!</p>
          </div>
          <p className="text-lg font-[600]">{title || "Recognize this contribution:"}</p>
          {alreadyGivePoint && (
            <div className="text-center mb-2">
              <p className="text-sm text-growth-green-600 font-[600]">
                ✓ You've already awarded {previousPoint} Twig{previousPoint === 1 ? '' : 's'} to this comment
              </p>
            </div>
          )}
          {!alreadyGivePoint && dailyLimits && (
            <div className="text-center mb-2">
              <p className="text-sm text-gray-600">
                {remainingPoints > 0 
                  ? `${remainingPoints} Twig${remainingPoints === 1 ? '' : 's'} remaining today for this user`
                  : `Daily limit reached for this user (${maxDailyLimit} Twigs max per day)`
                }
              </p>
            </div>
          )}
          <div className={`p-4 rounded-[8px] w-full ${alreadyGivePoint ? 'bg-growth-green-50 border border-growth-green-200' : 'bg-white'}`}>
            <div className="flex flex-col items-center">
              <p className="text-[20px] font-[800] text-growth-green-500">
                +{displayPoints}
                {alreadyGivePoint && displayPoints === previousPoint && (
                  <span className="ml-2 text-growth-green-600">✓</span>
                )}
              </p>
              <p className="text-lg font-semibold text-growth-green-900 mt-1">
                {getPointLevel(displayPoints)}
              </p>
              <p className="text-lg text-[#4B5563] text-center mt-1 px-1">
                {pointOptions.find((opt) => opt.value === displayPoints)?.description}
              </p>

              <div className="w-full mt-4">
                <div className="flex justify-between text-sm mb-1 text-growth-green-900">
                  <span>Basic</span>
                  <span>Outstanding</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full w-full overflow-hidden">
                  <div
                    className="h-2 rounded-full transition-all duration-300 ease-in-out"
                    style={{
                      background:
                        'linear-gradient(90deg, #C8E6C9 1.02%, #4CAF50 55.95%, #204A22 91.59%)',
                      width: `${(displayPoints / 5) * 100}%`
                    }}
                  ></div>
                </div>
              </div>

              <div className="flex w-full gap-4 mt-4">
                {pointOptions.map((option) => {
                  const disabled = isPointDisabled(option.value);
                  return (
                    <button
                      key={option.value}
                      onClick={() => !disabled && handlePointSelection(option.value)}
                      disabled={disabled}
                        className={`
                        w-14 h-14 rounded-full font-[800] text-sm md:text-lg flex items-center justify-center 
                        transform transition-all duration-300 ease-in-out
                        ${disabled 
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                          : (alreadyGivePoint && option.value === previousPoint) || (!alreadyGivePoint && displayPoints === option.value)
                            ? 'text-white shadow-md bg-growth-green' 
                            : 'bg-growth-green-50 text-gray-800 hover:shadow-sm'
                        }
                        ${!disabled && ((alreadyGivePoint && option.value === previousPoint) || (!alreadyGivePoint && displayPoints === option.value)) ? 'hover:-translate-y-1' : !disabled ? 'hover:-translate-y-0.5' : ''}
                        ${!disabled ? 'active:scale-95' : ''}
                      `}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <HStack w={'100%'} px={4}>
          <Button className="w-full !text-base h-[42px]" variant={'outline'} onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="w-full !text-base h-[42px]"
            isLoading={isLoading}
            disabled={alreadyGivePoint || !canAwardPoints || isPointDisabled(displayPoints)}
            onClick={() => {
              handleCreatePoint(displayPoints);
            }}
          >
            {alreadyGivePoint ? 'Already Awarded' : !canAwardPoints ? 'Daily Limit Reached' : 'Award Commenter'}
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default AwardModalOnSidePanel;
